import { useEffect, useState } from 'react';
import { useUser } from '../../auth/AuthProvider';
import { byStartTime, isMyJob, jobsInRange, jobTitle, periodRange, type Period } from '../../data/domain';
import { useClients, useJobs, useTechnicians } from '../../data/hooks';
import type { User } from '../../data/types';
import { addDays, atNoon, dateKey, money, plural, timeLabel, todayAtNoon } from '../../lib/format';
import { request } from '../../lib/api';
import { navigate } from '../../lib/router';
import { useSyncStatus } from '../../sync/status';
import { Icon } from '../../ui/Icon';
import { useUI } from '../../ui/UIProvider';
import { EmptyState, PageHeading, PanelHeader, View } from '../../ui/View';

export const PERIOD_OPTIONS: Array<{ value: Period; label: string }> = [
  { value: '7', label: 'Last 7 days' }, { value: '30', label: 'Last 30 days' }, { value: '90', label: 'Last 90 days' }, { value: 'fy', label: 'This financial year' }, { value: 'all', label: 'All time' }
];

/** A headline figure. The tone adds a coloured marker: success for done work, warning for work still open. */
export function Metric({ label, value, detail, tone }: { label: string; value: string | number; detail?: string; tone?: 'success' | 'warning' }) {
  return <article className={`metric${tone ? ` metric-${tone}` : ''}`}><p>{label}</p><strong>{value}</strong>{detail && <small>{detail}</small>}</article>;
}

export function Dashboard() {
  const user = useUser();
  const [period, setPeriod] = useState<Period>('fy');
  const isTech = user.role === 'tech';
  const today = todayAtNoon();
  return (
    <View id="dashboard">
      <PageHeading
        eyebrow={today.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        title={isTech ? 'My field work' : user.role === 'admin' ? 'Administration overview' : 'Operations overview'}
        subtitle={isTech ? 'Today’s jobs and this week’s progress in one place.' : 'Live business indicators for the selected reporting period.'}>
        {!isTech && (
          <label className="field-inline">Reporting period
            <select id="dashboardPeriod" value={period} onChange={(event) => setPeriod(event.target.value as Period)}>
              {PERIOD_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </label>
        )}
      </PageHeading>
      {isTech ? <TechnicianDashboard user={user} /> : <OperationsDashboard period={period} isAdmin={user.role === 'admin'} />}
    </View>
  );
}

function TechnicianDashboard({ user }: { user: User }) {
  const jobs = useJobs();
  const technicians = useTechnicians();
  const ui = useUI();
  const now = todayAtNoon();
  const weekEnd = addDays(now, 6);
  const mine = jobs.filter((job) => !job.cancelled && isMyJob(job, user, technicians));
  const todayJobs = mine.filter((job) => job.date === dateKey(now)).sort(byStartTime);
  const weekJobs = mine.filter((job) => { const date = atNoon(job.date); return date >= now && date <= weekEnd; });
  const completedToday = todayJobs.filter((job) => job.completed).length;
  const openToday = todayJobs.length - completedToday;
  const completedWeek = weekJobs.filter((job) => job.completed).length;
  const myName = technicians.find((tech) => tech.id === user.technicianId)?.name || user.displayName;

  function openRunSheet() {
    ui.open('runSheet', { tech: myName, date: dateKey(now) });
    navigate(`/run-sheets?date=${dateKey(now)}&tech=${encodeURIComponent(myName)}`);
  }

  return (
    <div className="stack">
      <div className="metrics">
        <Metric label="Completed today" value={completedToday} detail={todayJobs.length ? `${todayJobs.length} assigned today` : 'No work assigned today'} tone="success" />
        <Metric label="Remaining today" value={openToday} detail={openToday ? 'Ready to complete' : 'Nothing outstanding'} tone="warning" />
        <Metric label="This week" value={`${completedWeek} / ${weekJobs.length}`} detail={weekJobs.length ? 'Completed / scheduled jobs' : 'No work scheduled this week'} />
      </div>
      <div className="grid-2">
        <article className="panel">
          <PanelHeader title="Today’s field work" summary={`Jobs assigned to you for ${now.toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' })}`}>
            <button className="btn btn-secondary btn-sm" onClick={openRunSheet}>Open run sheet</button>
          </PanelHeader>
          <div className="row-list">
            {todayJobs.length ? todayJobs.map((job) => (
              <button key={job.id} className="row row-button job-row" onClick={() => ui.open('jobEditor', { mode: 'edit', jobId: job.id, techMode: true })}>
                <time>{timeLabel(job.startTime)}</time>
                <span className="row-text"><b>{jobTitle(job)}</b><small>{job.clientName}{job.notes ? ` · ${job.notes}` : ''}</small></span>
                <span className={`badge ${job.completed ? 'badge-success' : 'badge-neutral'}`}>{job.completed ? 'Completed' : 'Planned'}</span>
              </button>
            )) : <EmptyState>No jobs assigned to you today.</EmptyState>}
          </div>
        </article>
        <article className="panel">
          <PanelHeader title="Week at a glance" summary="Completed and outstanding work" />
          <ul className="row-list">
            {Array.from({ length: 7 }, (_, index) => {
              const date = addDays(now, index);
              const dayJobs = weekJobs.filter((job) => job.date === dateKey(date));
              const done = dayJobs.filter((job) => job.completed).length;
              return <li className="row week-row" key={index}><b>{date.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}</b><span>{done} completed</span><small>{dayJobs.length - done} remaining</small></li>;
            })}
          </ul>
        </article>
      </div>
    </div>
  );
}

function OperationsDashboard({ period, isAdmin }: { period: Period; isAdmin: boolean }) {
  const jobs = useJobs();
  const active = jobsInRange(jobs, periodRange(period)).filter((job) => !job.cancelled);
  const completed = active.filter((job) => job.completed);
  const outstanding = active.filter((job) => !job.completed);
  const hours = active.reduce((sum, job) => sum + (Number(job.duration) || 0), 0) / 60;
  const quoted = active.reduce((sum, job) => sum + (Number(job.quoteAmount) || 0), 0);
  const technicians = [...new Set(active.map((job) => job.techName).filter(Boolean))].sort();
  const pests = new Map<string, number>();
  active.forEach((job) => job.detail.split(',').map((item) => item.trim()).filter(Boolean).forEach((pest) => pests.set(pest, (pests.get(pest) || 0) + 1)));
  const pestRows = [...pests.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6);

  return (
    <div className="stack">
      <div className="metrics">
        <Metric label="Completed work" value={completed.length} detail={`${plural(active.length, 'active job')} in selected period`} tone="success" />
        <Metric label="Quoted value" value={money(quoted)} detail={active.length ? 'Active job quotes' : 'No active quotes in this period'} />
        <Metric label="Booked hours" value={`${hours.toFixed(1)} h`} detail={active.length ? 'Allocated field time' : 'No field time allocated'} />
        <Metric label="Outstanding jobs" value={outstanding.length} detail={outstanding.length ? 'Jobs awaiting completion' : 'All active work completed'} tone="warning" />
      </div>
      <div className="grid-2">
        <article className="panel">
          <PanelHeader title="Technician workload" summary="Job completion across the selected reporting period">
            <button className="btn-link" onClick={() => navigate(`/reports/technicians?period=${period}`)}>View report <Icon name="arrowRight" size={16} /></button>
          </PanelHeader>
          <ul className="row-list">
            {technicians.length ? technicians.map((tech) => {
              const assigned = active.filter((job) => job.techName === tech);
              const done = assigned.filter((job) => job.completed).length;
              const rate = assigned.length ? Math.round((done / assigned.length) * 100) : 0;
              return (
                <li className="row workload-row" key={tech}>
                  <span className="row-text"><b>{tech}</b><small>{assigned.length} assigned · {done} completed</small></span>
                  <span className="progress" aria-hidden="true"><i style={{ width: `${rate}%` }} /></span>
                  <strong>{rate}%</strong>
                </li>
              );
            }) : <EmptyState>No technician work recorded in this period.</EmptyState>}
          </ul>
        </article>
        <article className="panel">
          <PanelHeader title="Service demand" summary="Most common recorded pest services" />
          <ul className="row-list">
            {pestRows.length ? pestRows.map(([pest, count]) => <li className="row count-row" key={pest}><span>{pest}</span><b>{count}</b></li>) : <EmptyState>No pest services recorded in this period.</EmptyState>}
          </ul>
        </article>
      </div>
      {isAdmin && <AdminHealth jobsInPeriod={active.length} />}
    </div>
  );
}

function AdminHealth({ jobsInPeriod }: { jobsInPeriod: number }) {
  const clients = useClients();
  const status = useSyncStatus();
  const [api, setApi] = useState<'checking' | 'available' | 'unavailable'>('checking');
  useEffect(() => {
    let cancelled = false;
    request('/api/health', { sessionRequired: false }).then(() => !cancelled && setApi('available')).catch(() => !cancelled && setApi('unavailable'));
    return () => { cancelled = true; };
  }, [status.connection]);
  return (
    <article className="panel">
      <PanelHeader title="Platform maintenance" summary="Live browser and API checks for administration">
        <span className={`badge ${api === 'available' ? 'badge-success' : api === 'checking' ? 'badge-neutral' : 'badge-danger'}`} id="dashboardApiStatus">{api === 'checking' ? 'Checking API' : api === 'available' ? 'API available' : 'API needs attention'}</span>
      </PanelHeader>
      <dl className="stat-grid">
        <div><dt>Connection</dt><dd>{status.connection === 'online' ? 'Browser online' : 'Browser offline'}</dd></div>
        <div><dt>Client records</dt><dd>{clients.length}</dd></div>
        <div><dt>Jobs in period</dt><dd>{jobsInPeriod}</dd></div>
        <div><dt>Changes waiting to sync</dt><dd>{status.pending + status.conflicts + status.rejected}</dd></div>
        <div><dt>Page loaded</dt><dd>{new Date(performance.timeOrigin).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })}</dd></div>
      </dl>
    </article>
  );
}
